// --- User Management Dashboard ---
import { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc, addDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth, APP_ID } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import { Users, UserPlus, Edit2, Trash2, X, Shield, User, Loader2 } from 'lucide-react';
import Pagination from './Pagination';
import type { Team, AppUser as CurrentAppUser } from '../types';

// Path for users under artifacts (same as other data for consistent security rules)
const USERS_COLLECTION_PATH = `artifacts/${APP_ID}/public/data/users`;
const TX_COLLECTION_PATH = `artifacts/${APP_ID}/public/data/transactions`;

interface ManagedUser {
  uid: string;
  email: string;
  name: string;
  role: 'admin' | 'sub_admin' | 'owner' | 'player';
  teamId?: string;
  playerId?: string;
  createdAt?: string;
  mobile?: string;
}

interface UserManagementProps {
  teams: Team[];
  currentUser: CurrentAppUser;
  onBulkDeletePlayers?: () => Promise<void>;
  onBulkDeleteOwners?: () => Promise<void>;
}

const UserManagement: React.FC<UserManagementProps> = ({ teams, currentUser, onBulkDeleteOwners, onBulkDeletePlayers }) => {
  const toast = useToast();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [bulkDeletingRole, setBulkDeletingRole] = useState<'player' | 'owner' | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formRole, setFormRole] = useState<ManagedUser['role']>('owner');
  const [formTeamId, setFormTeamId] = useState('');
  const [formMobile, setFormMobile] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(18);

  // Paginate users
  const filteredUsers = useMemo(() => {
    if (currentUser.role === 'sub_admin') {
      // Sub-admins can only view owners/players and themselves
      return users.filter(u => u.role === 'owner' || u.role === 'player' || u.uid === currentUser.uid);
    }
    return users;
  }, [users, currentUser]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const deriveEmailFromMobile = (mobile: string) => mobile ? `${mobile}@ryametro.com` : '';
  const extractMobileFromEmail = (email: string) => {
    const [localPart] = email.split('@');
    return /^\d{10}$/.test(localPart) ? localPart : '';
  };

  const logUserTransaction = async (type: 'USER_CREATED' | 'USER_DELETED', details: Record<string, unknown>) => {
    try {
      await addDoc(collection(db, TX_COLLECTION_PATH), {
        type,
        timestamp: new Date().toISOString(),
        performedBy: currentUser.uid,
        performedByName: currentUser.name,
        season: '3',
        ...details
      });
    } catch (err) {
      console.error('Failed to log user transaction', err);
    }
  };

  // Fetch users from Firestore (using artifacts path for consistent security rules)
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, USERS_COLLECTION_PATH));
      const usersData: ManagedUser[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        usersData.push({
          uid: docSnap.id,
          email: data.email || '',
          name: data.name || '',
          role: data.role || 'player',
          teamId: data.teamId,
          playerId: data.playerId,
          createdAt: data.createdAt,
          mobile: data.mobile
        });
      });
      setUsers(usersData.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to fetch users');
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Open modal for creating a new user
  const handleOpenCreate = () => {
    setModalMode('create');
    setEditingUser(null);
    setFormName('');
    // Sub-admins can only create owners or players
    setFormRole(currentUser.role === 'sub_admin' ? 'owner' : 'owner');
    setFormTeamId('');
    setFormMobile('');
    setShowModal(true);
  };

  // Open modal for editing a user
  const handleOpenEdit = (user: ManagedUser) => {
    setModalMode('edit');
    setEditingUser(user);
    setFormName(user.name);
    // Clamp editable role for sub-admins
    if (currentUser.role === 'sub_admin' && (user.role === 'admin' || user.role === 'sub_admin')) {
      setFormRole('owner');
    } else {
      setFormRole(user.role);
    }
    setFormTeamId(user.teamId || '');
    setFormMobile(user.mobile || extractMobileFromEmail(user.email) || '');
    setShowModal(true);
  };

  // Create new user with Firebase Auth + Firestore doc
  const handleCreateUser = async () => {
    if (!formMobile || !formName) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!/^\d{10}$/.test(formMobile)) {
      toast.error('Mobile number must be 10 digits');
      return;
    }
    const derivedEmail = deriveEmailFromMobile(formMobile);
    const derivedPassword = formMobile;
    // Prevent privilege escalation by sub-admins
    if (currentUser.role === 'sub_admin' && formRole === 'admin') {
      toast.error('Sub-admins cannot create admin users');
      return;
    }
    if (currentUser.role === 'sub_admin' && formRole === 'sub_admin') {
      toast.error('Sub-admins cannot create sub-admin users');
      return;
    }

    setSaving(true);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, derivedEmail, derivedPassword);
      const uid = userCredential.user.uid;

      // Create Firestore document under artifacts path
      await setDoc(doc(db, USERS_COLLECTION_PATH, uid), {
        email: derivedEmail,
        name: formName,
        role: formRole,
        teamId: formRole === 'owner' ? formTeamId || null : null,
        mobile: formMobile,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });

      toast.success(`User ${formName} created successfully`);
      await logUserTransaction('USER_CREATED', { userEmail: derivedEmail, userName: formName, userRole: formRole });
      setShowModal(false);
      fetchUsers();
    } catch (err: unknown) {
      console.error('Error creating user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      toast.error(errorMessage);
    }
    setSaving(false);
  };

  // Update existing user in Firestore
  const handleUpdateUser = async () => {
    if (!editingUser || !formName) {
      toast.error('Please fill all required fields');
      return;
    }
    if (currentUser.role === 'sub_admin' && formRole === 'admin') {
      toast.error('Sub-admins cannot promote users to admin');
      return;
    }
    if (currentUser.role === 'sub_admin' && (formRole === 'admin' || formRole === 'sub_admin')) {
      toast.error('Sub-admins can only assign owner or player roles');
      return;
    }

    setSaving(true);
    try {
      await updateDoc(doc(db, USERS_COLLECTION_PATH, editingUser.uid), {
        name: formName,
        role: formRole,
        teamId: formRole === 'owner' ? formTeamId || null : null,
        mobile: formMobile || extractMobileFromEmail(editingUser.email) || null,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid
      });

      toast.success(`User ${formName} updated successfully`);
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user');
    }
    setSaving(false);
  };

  // Delete user from Firestore (Note: doesn't delete from Auth)
  const handleDeleteUser = async (user: ManagedUser) => {
    if (user.uid === currentUser.uid) {
      toast.error('Cannot delete your own account');
      return;
    }

    setConfirmDeleteId(user.uid);

    if (confirmDeleteId === user.uid) {
      setDeletingId(user.uid);
      try {
        await deleteDoc(doc(db, USERS_COLLECTION_PATH, user.uid));
        toast.success(`User ${user.name} deleted`);
        await logUserTransaction('USER_DELETED', { userEmail: user.email, userName: user.name, userRole: user.role });
        fetchUsers();
      } catch (err) {
        console.error('Error deleting user:', err);
        toast.error('Failed to delete user');
      }
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      admin: 'bg-red-100 text-red-700',
      sub_admin: 'bg-orange-100 text-orange-700',
      owner: 'bg-blue-100 text-blue-700',
      player: 'bg-green-100 text-green-700'
    };
    return styles[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">User Management</h2>
              <p className="text-white/80">Create and manage team owners and users</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2 bg-white text-violet-600 rounded-lg font-medium hover:bg-violet-50 transition-colors"
            >
              <UserPlus className="w-5 h-5" /> New User
            </button>
            {currentUser.role === 'admin' && (
              <>
                <button
                  onClick={async () => {
                    if (!onBulkDeletePlayers) return;
                    setBulkDeletingRole('player');
                    await onBulkDeletePlayers();
                    setBulkDeletingRole(null);
                  }}
                  disabled={bulkDeletingRole === 'player'}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-60"
                >
                  {bulkDeletingRole === 'player' ? 'Deleting...' : 'Delete Player Users'}
                </button>
                <button
                  onClick={async () => {
                    if (!onBulkDeleteOwners) return;
                    setBulkDeletingRole('owner');
                    await onBulkDeleteOwners();
                    setBulkDeletingRole(null);
                  }}
                  disabled={bulkDeletingRole === 'owner'}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-60"
                >
                  {bulkDeletingRole === 'owner' ? 'Deleting...' : 'Delete Owner Users'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2" />
            <p className="text-slate-500">Loading users...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Team</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedUsers.map(user => (
                  <tr key={user.uid} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{user.name}</p>
                          <p className="text-xs text-slate-500">{user.uid.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{user.email}</td>
                    <td className="px-4 py-3 text-center">
                      {(currentUser.role === 'sub_admin' && user.uid === currentUser.uid) ? (
                        <span className="text-slate-400 text-xs italic">hidden</span>
                      ) : (
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                          {user.role.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {user.teamId ? teams.find(t => t.id === user.teamId)?.name || '-' : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleOpenEdit(user)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                    onClick={() => handleDeleteUser(user)}
                    disabled={deletingId === user.uid || user.uid === currentUser.uid}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete"
                  >
                    {confirmDeleteId === user.uid ? (
                      <span className="text-xs font-semibold text-red-600">Confirm?</span>
                    ) : deletingId === user.uid ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                      </div>
                    </td>
                  </tr>
                ))}
        {filteredUsers.length === 0 && (
          <tr>
            <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No users found</p>
            </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="p-4 border-t border-slate-200">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredUsers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">
                {modalMode === 'create' ? 'Create New User' : 'Edit User'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Mobile (drives email/password) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile (10 digits) *</label>
                <input
                  type="tel"
                  value={formMobile}
                  onChange={e => setFormMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="9876543210"
                  disabled={modalMode === 'edit'}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Login email & password will be: {deriveEmailFromMobile(formMobile) || 'mobile@ryametro.com'} / {formMobile || 'mobile'}
                </p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Name *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role *</label>
                <select
                  value={formRole}
                  onChange={e => setFormRole(e.target.value as ManagedUser['role'])}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="owner">Team Owner</option>
                  <option value="player">Player</option>
                  {currentUser.role !== 'sub_admin' && <option value="sub_admin">Sub Admin</option>}
                  {currentUser.role !== 'sub_admin' && <option value="admin">Admin</option>}
                </select>
                </div>

              {/* Team (only for owners) */}
              {formRole === 'owner' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assign Team</label>
                  <select
                    value={formTeamId}
                    onChange={e => setFormTeamId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">-- Select Team --</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={modalMode === 'create' ? handleCreateUser : handleUpdateUser}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {modalMode === 'create' ? 'Create User' : 'Update User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
